import type { QueryKey } from '@tanstack/react-query';

import type { PageMessage } from '../../../types/PageMessage';

import type { ComboBoxProps } from './ComboBox';
import { PaginatedAsyncComboBoxBase } from './PaginatedAsyncComboBoxBase';
import { ComboBoxMode } from './types/ComboBoxMode';
import type { ComboBoxOption } from './types/ComboBoxOption';

const DEFAULT_PAGINATED_DROPDOWN_MAX_HEIGHT = '608px';

interface PaginatedAsyncComboBoxOptionsProps<Option extends ComboBoxOption> {
    optionsQueryKey: (search: string) => QueryKey;
    optionsQueryFn: (params: { search: string; page: number }) => Promise<PageMessage<Option>>;
    customOptionsFilter?: (options: Option[], search: string) => Option[];
    prefetchOnHover?: boolean;
}

export type PaginatedAsyncComboBoxProps<Option extends ComboBoxOption = ComboBoxOption> = Omit<
    ComboBoxProps<Option>,
    'options'
> &
    PaginatedAsyncComboBoxOptionsProps<Option> & {
        selectedOptionQueryKey: (value: NonNullable<Option['value']>) => QueryKey;
        selectedOptionQueryFn: (value: NonNullable<Option['value']>) => Promise<Option | null>;
        showSelectedOptionAtTop?: boolean;
    };

export function PaginatedAsyncComboBox<Option extends ComboBoxOption = ComboBoxOption>({
    value,
    optionsQueryKey,
    optionsQueryFn,
    customOptionsFilter,
    selectedOptionQueryKey,
    selectedOptionQueryFn,
    prefetchOnHover,
    showSelectedOptionAtTop,
    onChange,
    isClearable = false,
    dropdownMaxHeight = DEFAULT_PAGINATED_DROPDOWN_MAX_HEIGHT,
    ...props
}: PaginatedAsyncComboBoxProps<Option>) {
    return (
        <PaginatedAsyncComboBoxBase<Option>
            {...props}
            mode={ComboBoxMode.Single}
            value={value}
            optionsQueryKey={optionsQueryKey}
            optionsQueryFn={optionsQueryFn}
            selectedOptionQueryKey={selectedOptionQueryKey}
            selectedOptionQueryFn={selectedOptionQueryFn}
            customOptionsFilter={customOptionsFilter}
            isClearable={isClearable}
            dropdownMaxHeight={dropdownMaxHeight}
            prefetchOnHover={prefetchOnHover}
            showSelectedOptionAtTop={showSelectedOptionAtTop}
            onSelectionChange={(nextSelectedOptions, meta) => {
                onChange?.(nextSelectedOptions[0]?.value ?? null, meta);
            }}
        />
    );
}
